import {Button} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {maybePluralize} from '@wandb/weave/core/util/string';
import React, {FC, memo, useState} from 'react';

import {envIsLocal} from '../../../config';
import {Organization} from '../../../generated/graphql';
import {TeamPageEntity} from '../../../graphql/teamQuery';
import {TeamsPurchaseSeatModal} from '../../../pages/Billing/TeamsPurchaseSeatModal';
import {
  getModelSeatsSub,
  getPrimarySub,
  isProPrimarySubscription,
  orgPrimarySubBillingInterval,
  orgPrimarySubUnitPrice,
} from '../../../util/accounts/pricing';
import {checkIsServiceAccount} from '../../../util/serviceAccounts';
import {AddUserDrawer} from '../../AddUserDrawer';

interface TeamMembersInviteInputProps {
  entity: TeamPageEntity;
  refetch?: () => void;
  organization: Organization;
  isViewerAdmin: boolean;
}
export const TeamMembersTableHeader: FC<TeamMembersInviteInputProps> = memo(
  ({entity, refetch, organization, isViewerAdmin}) => {
    const primarySub =
      organization != null ? getPrimarySub(organization) : null;

    const isProPlan =
      primarySub != null && isProPrimarySubscription(primarySub);

    const modelsSeatsSubscription =
      isProPlan && organization != null
        ? getModelSeatsSub(organization)
        : primarySub;

    const [openPurchaseSeatModal, setOpenPurchaseSeatModal] =
      useState<boolean>(false);

    const availableSeats = organization?.seatAvailability?.seats;

    const planUnitPrice = orgPrimarySubUnitPrice(organization);
    const billingInterval = orgPrimarySubBillingInterval(organization);
    const availableSeatsToPurchase = primarySub?.availableSeatsToPurchase ?? 0;
    const canPurchaseMoreSeat =
      planUnitPrice != null && 0 < availableSeatsToPurchase;

    const shouldShowBuySeatButton = availableSeats === 0 && !envIsLocal;

    return (
      <>
        <Tailwind>
          <div className="mb-8 flex items-end justify-between">
            <div className="flex items-end">
              <div className="text-base font-semibold leading-6 text-moon-800">
                {maybePluralize(
                  entity.members.filter(
                    m => !checkIsServiceAccount(m.accountType)
                  ).length,
                  'user'
                )}
              </div>
              {isViewerAdmin && !envIsLocal && (
                <div className="flex text-moon-800">
                  .&nbsp;{maybePluralize(availableSeats, 'seat')} available.
                </div>
              )}
            </div>
            <div className="flex items-end">
              <AddUserDrawer
                disabled={false}
                existingTeamName={entity?.name}
                refetch={refetch ?? undefined}
                analyticsLocation={'team members table invitation drawer'}
                organization={organization}
              />
              {canPurchaseMoreSeat &&
                primarySub &&
                isViewerAdmin &&
                shouldShowBuySeatButton && (
                  <Button
                    className="ml-4"
                    startIcon="users-team"
                    variant="primary"
                    onClick={() => setOpenPurchaseSeatModal(true)}>
                    Buy seats
                  </Button>
                )}
            </div>
          </div>
        </Tailwind>
        {openPurchaseSeatModal &&
          modelsSeatsSubscription &&
          planUnitPrice != null &&
          billingInterval != null && (
            <TeamsPurchaseSeatModal
              onConfirm={async () => await refetch?.()}
              modelsSeatsSubscription={modelsSeatsSubscription}
              proposedSeats={modelsSeatsSubscription?.seats + 1}
              organizationId={organization.id}
              organizationName={organization.name}
              onClose={() => setOpenPurchaseSeatModal(false)}
            />
          )}
      </>
    );
  }
);
